import React, { useState, useEffect } from 'react';
import { Form, Input, Button, message, Upload, Space, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { Project, ProjectUpdate, ProjectType } from '../../types/project';
import projectService from '../../services/projectService';
import { UploadOutlined } from '@ant-design/icons';
import { API_CONFIG } from '../../config';
import { getAuthToken } from '../../utils/auth';

interface EditProjectFormProps {
    project: Project;
    onSuccess: () => void;
    onCancel: () => void;
}

interface ProjectFormData extends ProjectUpdate {
    project_type?: ProjectType;
}

const EditProjectForm: React.FC<EditProjectFormProps> = ({ project, onSuccess, onCancel }) => {
    const [form] = Form.useForm<ProjectFormData>();
    const [loading, setLoading] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const { t } = useTranslation();

    useEffect(() => {
        form.setFieldsValue({
            name: project.name,
            description: project.description,
            domain: project.domain,
            prefix: project.prefix,
            project_type: project.project_type,
            image_url: project.image_url
        });
    }, [project, form]);

    useEffect(() => {
        return () => {
            if (selectedFile) {
                URL.revokeObjectURL(URL.createObjectURL(selectedFile));
            }
        };
    }, [selectedFile]);

    const handleSubmit = async (values: ProjectFormData) => {
        try {
            setLoading(true);
            const formData = new FormData();
            
            // Always include all fields, even if they haven't changed
            formData.append('name', values.name || project.name);
            formData.append('description', values.description || project.description || '');
            formData.append('domain', values.domain || project.domain || '');
            formData.append('prefix', values.prefix || project.prefix || '');
            formData.append('project_type', values.project_type || project.project_type || ProjectType.OTHER);
            
            if (selectedFile) {
                formData.append('file', selectedFile);
            }

            console.log('Submitting form data:', {
                name: values.name || project.name,
                description: values.description || project.description,
                domain: values.domain || project.domain,
                prefix: values.prefix || project.prefix,
                project_type: values.project_type || project.project_type || ProjectType.OTHER,
                hasFile: !!selectedFile
            });

            await projectService.updateProject(project.id, formData);
            message.success(t('projects.updateSuccess'));
            onSuccess();
        } catch (error: any) {
            console.error('Error updating project:', error);
            console.error('Error details:', error.response?.data);
            message.error(error.response?.data?.detail || t('projects.updateError'));
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = (file: File) => {
        setSelectedFile(file);
        return false;
    };

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
        >
            <Form.Item
                name="name"
                label={t('projects.name')}
                rules={[{ required: true, message: t('projects.nameRequired') }]}
            >
                <Input placeholder={t('projects.namePlaceholder')} />
            </Form.Item>

            <Form.Item
                name="prefix"
                label={t('projects.prefix')}
                rules={[
                    { required: true, message: t('projects.prefixRequired') },
                    { max: 10, message: t('projects.prefixMaxLength') }
                ]}
            >
                <Input placeholder={t('projects.prefixPlaceholder')} maxLength={10} />
            </Form.Item>

            <Form.Item
                name="project_type"
                label={t('projects.type')}
                rules={[{ required: true, message: t('projects.typeRequired') }]}
            >
                <Select>
                    <Select.Option value={ProjectType.WEB}>{t('projects.types.web')}</Select.Option>
                    <Select.Option value={ProjectType.ANDROID}>{t('projects.types.android')}</Select.Option>
                    <Select.Option value={ProjectType.IOS}>{t('projects.types.ios')}</Select.Option>
                    <Select.Option value={ProjectType.MACOS}>{t('projects.types.macos')}</Select.Option>
                    <Select.Option value={ProjectType.WINDOWS}>{t('projects.types.windows')}</Select.Option>
                    <Select.Option value={ProjectType.LINUX}>{t('projects.types.linux')}</Select.Option>
                    <Select.Option value={ProjectType.DESKTOP}>{t('projects.types.desktop')}</Select.Option>
                    <Select.Option value={ProjectType.MOBILE}>{t('projects.types.mobile')}</Select.Option>
                    <Select.Option value={ProjectType.BACKEND}>{t('projects.types.backend')}</Select.Option>
                    <Select.Option value={ProjectType.API}>{t('projects.types.api')}</Select.Option>
                    <Select.Option value={ProjectType.EMBEDDED}>{t('projects.types.embedded')}</Select.Option>
                    <Select.Option value={ProjectType.GAME}>{t('projects.types.game')}</Select.Option>
                    <Select.Option value={ProjectType.OTHER}>{t('projects.types.other')}</Select.Option>
                </Select>
            </Form.Item>

            <Form.Item
                name="domain"
                label={t('projects.domain')}
            >
                <Input placeholder={t('projects.domainPlaceholder')} />
            </Form.Item>

            <Form.Item
                name="image_url"
                label={t('projects.image')}
            >
                <Upload
                    name="file"
                    listType="picture-card"
                    showUploadList={false}
                    beforeUpload={handleUpload}
                    style={{ width: '100%' }}
                    fileList={[]}
                >
                    {selectedFile ? (
                        <img
                            src={URL.createObjectURL(selectedFile)}
                            alt="preview"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    ) : project.image_url ? (
                        <img
                            src={selectedFile ? URL.createObjectURL(selectedFile) : project.image_url}
                            alt="preview"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    ) : (
                        <div>
                            <UploadOutlined />
                            <div style={{ marginTop: 8 }}>{t('projects.uploadImage')}</div>
                        </div>
                    )}
                </Upload>
            </Form.Item>

            <Form.Item
                name="description"
                label={t('projects.description')}
            >
                <Input.TextArea rows={4} placeholder={t('projects.descriptionPlaceholder')} />
            </Form.Item>

            <Form.Item>
                <Space>
                    <Button type="primary" htmlType="submit" loading={loading}>
                        {t('projects.save')}
                    </Button>
                    <Button onClick={onCancel}>
                        {t('projects.cancel')}
                    </Button>
                </Space>
            </Form.Item>
        </Form>
    );
};

export default EditProjectForm; 